import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { withJWTClient } from "@/api/connectClient";
import { CONNECT_API_V1_BASE_URL } from "@/api/gateway";
import {
  notificationRowSchema,
  type NotificationRow,
} from "@/features/Notifications/blocks/schema";
import { useIsNotificationsEnabledForSite } from "@/features/Notifications/flag";

const API_BASE_URL = CONNECT_API_V1_BASE_URL;

export const UNSEEN_COUNT_POLL_INTERVAL_MS = 30_000;

export interface ListNotificationsParams {
  pageSize?: number;
  pageToken?: string;
  includeInfo?: boolean;
}

export const notificationKeys = {
  all: () => ["notifications"] as const,
  lists: (siteId: string) => ["notifications", "list", siteId] as const,
  list: (siteId: string, params: ListNotificationsParams) =>
    ["notifications", "list", siteId, params] as const,
  unseenCount: (siteId: string) =>
    ["notifications", "unseen-count", siteId] as const,
  prefs: () => ["notifications", "prefs"] as const,
};

const listNotificationsResponseSchema = z.object({
  notifications: z.array(notificationRowSchema).default([]),
  next_page_token: z.string().default(""),
});

// uint32 on the wire, so protojson emits a number; older gateways stringify.
const unseenCountResponseSchema = z.object({
  count: z.coerce.number().default(0),
});

const markReadResponseSchema = z.object({
  read_at: z.string().nullish(),
});

const markAllReadResponseSchema = z.object({
  marked: z.coerce.number().default(0),
});

const notificationPrefsSchema = z.object({
  info_enabled: z.boolean().default(false),
});

export type NotificationsPage = z.infer<typeof listNotificationsResponseSchema>;
export type NotificationPrefs = z.infer<typeof notificationPrefsSchema>;

export async function listNotifications(
  siteId: string,
  params: ListNotificationsParams = {},
): Promise<NotificationsPage> {
  const query = new URLSearchParams();
  if (siteId) query.set("site_id", siteId);
  if (params.pageSize != null) query.set("page_size", String(params.pageSize));
  if (params.pageToken) query.set("page_token", params.pageToken);
  if (params.includeInfo) query.set("include_info", "true");
  const qs = query.toString();

  const response = await withJWTClient((client) =>
    client.get(`${API_BASE_URL}/notifications${qs ? `?${qs}` : ""}`),
  );
  return listNotificationsResponseSchema.parse(response.data);
}

export async function getUnseenCount(siteId: string): Promise<number> {
  const qs = siteId
    ? `?${new URLSearchParams({ site_id: siteId }).toString()}`
    : "";
  const response = await withJWTClient((client) =>
    client.get(`${API_BASE_URL}/notifications:unseen-count${qs}`),
  );
  return unseenCountResponseSchema.parse(response.data).count;
}

export async function markNotificationRead(id: string): Promise<string> {
  const response = await withJWTClient((client) =>
    client.post(
      `${API_BASE_URL}/notifications/${encodeURIComponent(id)}:mark-read`,
      {},
    ),
  );
  return markReadResponseSchema.parse(response.data).read_at ?? "";
}

export async function markAllNotificationsRead(
  siteId: string,
): Promise<number> {
  const query = new URLSearchParams();
  if (siteId) query.set("site_id", siteId);
  const qs = query.toString();
  const response = await withJWTClient((client) =>
    client.post(
      `${API_BASE_URL}/notifications:mark-all-read${qs ? `?${qs}` : ""}`,
      {},
    ),
  );
  return markAllReadResponseSchema.parse(response.data).marked;
}

export async function getNotificationPrefs(): Promise<NotificationPrefs> {
  const response = await withJWTClient((client) =>
    client.get(`${API_BASE_URL}/notifications/preferences`),
  );
  return notificationPrefsSchema.parse(response.data);
}

export async function setNotificationPrefs(
  infoEnabled: boolean,
): Promise<NotificationPrefs> {
  const response = await withJWTClient((client) =>
    client.post(`${API_BASE_URL}/notifications/preferences`, {
      info_enabled: infoEnabled,
    }),
  );
  return notificationPrefsSchema.parse(response.data);
}

export const notificationsQueryOptions = (
  siteId: string,
  params: ListNotificationsParams = {},
) => ({
  queryKey: notificationKeys.list(siteId, params),
  queryFn: () => listNotifications(siteId, params),
});

// The global staleTime is 5 minutes, so `true` would leave the badge stale after a focus.
export const unseenCountQueryOptions = (siteId: string) => ({
  queryKey: notificationKeys.unseenCount(siteId),
  queryFn: () => getUnseenCount(siteId),
  refetchInterval: UNSEEN_COUNT_POLL_INTERVAL_MS,
  refetchOnWindowFocus: "always" as const,
  staleTime: 0,
});

export function useUnseenCount(siteId: string) {
  const flagEnabled = useIsNotificationsEnabledForSite(siteId);
  return useQuery({
    ...unseenCountQueryOptions(siteId),
    enabled: flagEnabled,
  });
}

export function useNotifications(
  siteId: string,
  options: { includeInfo?: boolean; pageSize?: number; enabled?: boolean } = {},
) {
  const { enabled = true, ...params } = options;
  const flagEnabled = useIsNotificationsEnabledForSite(siteId);
  return useQuery({
    ...notificationsQueryOptions(siteId, params),
    enabled: flagEnabled && enabled,
  });
}

export function useMarkRead(siteId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => markNotificationRead(id),
    onMutate: async (id) => {
      const filter = { queryKey: notificationKeys.lists(siteId) };
      await queryClient.cancelQueries(filter);
      const previous = queryClient.getQueriesData<NotificationsPage>(filter);
      const readAt = new Date().toISOString();
      queryClient.setQueriesData<NotificationsPage>(filter, (page) =>
        page
          ? {
              ...page,
              notifications: page.notifications.map(
                (row: NotificationRow): NotificationRow =>
                  row.id === id && !row.read_at
                    ? { ...row, read_at: readAt }
                    : row,
              ),
            }
          : page,
      );
      return { previous };
    },
    onError: (_error, _id, context) => {
      for (const [key, data] of context?.previous ?? []) {
        queryClient.setQueryData(key, data);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: notificationKeys.lists(siteId),
      });
      void queryClient.invalidateQueries({
        queryKey: notificationKeys.unseenCount(siteId),
      });
    },
  });
}

export function useMarkAllRead(siteId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => markAllNotificationsRead(siteId),
    onMutate: async () => {
      const filter = { queryKey: notificationKeys.lists(siteId) };
      await queryClient.cancelQueries(filter);
      const previous = queryClient.getQueriesData<NotificationsPage>(filter);
      const readAt = new Date().toISOString();
      queryClient.setQueriesData<NotificationsPage>(filter, (page) =>
        page
          ? {
              ...page,
              notifications: page.notifications.map(
                (row: NotificationRow): NotificationRow =>
                  row.read_at ? row : { ...row, read_at: readAt },
              ),
            }
          : page,
      );
      const previousCount = queryClient.getQueryData<number>(
        notificationKeys.unseenCount(siteId),
      );
      queryClient.setQueryData(notificationKeys.unseenCount(siteId), 0);
      return { previous, previousCount };
    },
    onError: (_error, _variables, context) => {
      for (const [key, data] of context?.previous ?? []) {
        queryClient.setQueryData(key, data);
      }
      queryClient.setQueryData(
        notificationKeys.unseenCount(siteId),
        context?.previousCount,
      );
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: notificationKeys.lists(siteId),
      });
      void queryClient.invalidateQueries({
        queryKey: notificationKeys.unseenCount(siteId),
      });
    },
  });
}

export function useNotificationPrefs(siteId: string) {
  const flagEnabled = useIsNotificationsEnabledForSite(siteId);
  return useQuery({
    queryKey: notificationKeys.prefs(),
    queryFn: getNotificationPrefs,
    enabled: flagEnabled,
  });
}

export function useSetInfoEnabled(siteId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (infoEnabled: boolean) => setNotificationPrefs(infoEnabled),
    onSuccess: (prefs) => {
      queryClient.setQueryData(notificationKeys.prefs(), prefs);
      void queryClient.invalidateQueries({
        queryKey: notificationKeys.lists(siteId),
      });
      void queryClient.invalidateQueries({
        queryKey: notificationKeys.unseenCount(siteId),
      });
    },
  });
}
